import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import { useMMKVObject, useMMKVString } from 'react-native-mmkv';

import { joinWaitlistMutation } from '@/api/booking/joinWaitlistMutation';
import type { JoinWaitlistPayload } from '@/api/booking/joinWaitlistMutation/types';
import { isWaitlistConflictError } from '@/api/booking/joinWaitlistMutation/types';
import type { CartCustomerPayload } from '@/api/booking/upsertCartMutation/types';
import { useToast } from '@/components/_shared/Toaster';
import { storage, storageKeys } from '@/constants/storage';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { canTipCartItem, transformServiceIds } from '@/utils/bookingCartUtils';
import {
  classifyCheckoutError,
  getCheckoutErrorContent,
} from '@/utils/checkoutErrors';
import { Logger } from '@/utils/logging';
import type { PaymentMethod } from '@/utils/paymentMethods';
import { Tracker } from '@/utils/tracking';

import type { GuestUserInfo, UseBookingPaymentOptions } from './types';

type UseWaitlistPaymentOptions = Pick<
  UseBookingPaymentOptions,
  | 'refs'
  | 'cart'
  | 'shop'
  | 'services'
  | 'waitlistSelections'
  | 'onWaitlistSuccess'
>;

export const useWaitlistPayment = (options: UseWaitlistPaymentOptions) => {
  const { refs, cart, shop, services, waitlistSelections, onWaitlistSuccess } =
    options;

  const { t } = useScreenTranslations('ChooseTimeScreen');
  const { toast } = useToast();

  const [existingCustomerId] = useMMKVString(storageKeys.customerId, storage);
  const [, setGuestUserInfo] = useMMKVObject<GuestUserInfo>(
    storageKeys.guestUserInfo,
    storage,
  );
  const wasLoggedInAtStart = useRef(!!existingCustomerId);

  // Stores customer data between handleBook and handleSuccessWaitlistPayment
  const pendingWaitlistCustomerRef = useRef<CartCustomerPayload | null>(null);

  const { mutate: mutateWaitlist, isPending: isJoiningWaitlist } = useMutation(
    joinWaitlistMutation({
      onSuccess: (data) => {
        Tracker.track('waitlist_success', {
          shopId: shop?.id ?? '',
          waitlistCustomerId: data.id,
        });

        if (!wasLoggedInAtStart.current && pendingWaitlistCustomerRef.current) {
          setGuestUserInfo(pendingWaitlistCustomerRef.current);
        }
        onWaitlistSuccess(data.id);
      },
      onError: (error) => {
        if (isWaitlistConflictError(error)) {
          toast({
            variant: 'error',
            message: t('error.alreadyOnWaitlist'),
          });
          return;
        }

        Tracker.track('waitlist_failure', {
          shopId: shop?.id ?? '',
          error: String(error),
        });
        Logger.error('Failed to join waitlist', { error });
        toast({
          variant: 'error',
          message: getCheckoutErrorContent(classifyCheckoutError(error), t)
            .description,
        });
      },
    }),
  );

  const buildWaitlistPayload = useCallback(
    (
      customer: CartCustomerPayload,
      appointmentIndex: number,
      selection: { barberId: string; day: string },
      payment?: { paymentToken?: string; paymentCardId?: string },
    ): JoinWaitlistPayload | null => {
      const cartItem = cart?.items[appointmentIndex];
      if (!cartItem) return null;

      const rawServiceIds = cartItem.services.map((s) => s.id);
      const serviceIds = transformServiceIds(rawServiceIds, services);

      const payload: JoinWaitlistPayload = {
        shopId: shop?.id ?? '',
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        day: selection.day,
        serviceIds,
        tipAmount: canTipCartItem(cartItem)
          ? cartItem.tipAmount || cartItem.defaultTip?.amount || undefined
          : undefined,
        preferredLanguage: customer.preferredLanguage,
        ...payment,
      };

      if (selection.barberId) {
        payload.barberId = selection.barberId;
      }

      return payload;
    },
    [cart?.items, shop?.id, services],
  );

  const joinAllWaitlistEntries = useCallback(
    (
      customer: CartCustomerPayload,
      payment?: { paymentToken?: string; paymentCardId?: string },
    ) => {
      for (const [appointmentIndex, selection] of waitlistSelections) {
        const payload = buildWaitlistPayload(
          customer,
          appointmentIndex,
          selection,
          payment,
        );
        if (payload) {
          mutateWaitlist(payload);
        }
      }
    },
    [waitlistSelections, buildWaitlistPayload, mutateWaitlist],
  );

  const handleSuccessWaitlistPayment = useCallback(
    async (paymentMethod: PaymentMethod) => {
      const customer = pendingWaitlistCustomerRef.current;
      if (!customer) return;

      const payment =
        'card' in paymentMethod
          ? { paymentCardId: paymentMethod.card.id }
          : {
              paymentToken: (paymentMethod as { token?: string }).token,
            };

      refs.confirmBookingBottomSheetRef.current?.close();
      refs.checkoutFormBottomSheetRef.current?.close();

      joinAllWaitlistEntries(customer, payment);
    },
    [joinAllWaitlistEntries, refs],
  );

  const setPendingWaitlistCustomer = useCallback(
    (customer: CartCustomerPayload | null) => {
      pendingWaitlistCustomerRef.current = customer;
    },
    [],
  );

  return {
    setPendingWaitlistCustomer,
    isJoiningWaitlist,
    joinAllWaitlistEntries,
    handleSuccessWaitlistPayment,
  };
};
